import axios from "axios";
import { toast } from "react-toastify";
import { getFromStorage, handleLogout, isAuthenticated , changeLanguage , getUserId, getBrowserTimezone } from "../utils/Helper";
import {
    isOrdersListApiUrl,
    isQuotesListApiUrl,
    settleOrdersListVisit,
    settleQuotesListVisit,
} from "../utils/listPageLoading";
import { Config } from "../config/Config";
import { VALIDATION_MESSAGE , CODE , StorageConstant } from "../constants/Constants";

const apiinstance = axios.create({
    baseURL: Config?.REACT_APP_API_BASE_URL,
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "*",
        "Access-Control-Allow-Methods": "PUT, GET, POST, DELETE, OPTIONS",
        ...(isAuthenticated() && { "Authorization": "Bearer " + isAuthenticated() }),
        ...(changeLanguage() && { "lang": changeLanguage() }),
        "device_type": "web",
        "x-is-guest-user":"1",
        ...(getUserId() && { "x-user-id": getUserId() }),
        ...(getBrowserTimezone() && { timezone: getBrowserTimezone() }),

    }
})


apiinstance.interceptors.request.use(
    config => {
        const token = getFromStorage({ key: StorageConstant?.ACCESS_TOKEN, type: 'cookies' });
        const currentLang = changeLanguage();
        const currentUserId = getUserId();
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        if (currentLang) {
            config.headers['lang'] = currentLang;
        } else if (config.headers?.lang) {
            delete config.headers.lang;
        }
        if (currentUserId) {
            config.headers['x-user-id'] = currentUserId;
        } else if (config.headers?.['x-user-id']) {
            delete config.headers['x-user-id'];
        }
        const browserTimezone = getBrowserTimezone();
        if (browserTimezone) {
            config.headers.timezone = browserTimezone;
        } else if (config.headers?.timezone) {
            delete config.headers.timezone;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

const apiinstanceFormData = axios.create({
    baseURL: Config?.REACT_APP_API_BASE_URL,
    headers: {
        Accept: "application/json",
        'Content-Type': 'multipart/form-data',
        "Access-Control-Allow-Origin": "",
        "Access-Control-Allow-Headers": "",
        "Access-Control-Allow-Methods": "PUT, GET, POST, DELETE, OPTIONS",
        ...(isAuthenticated() && { "Authorization": "Bearer " + isAuthenticated() }),
        ...(changeLanguage() && { "lang": changeLanguage() }),
        "device_type": "web",
        "x-is-guest-user":"1",
        ...(getUserId() && { "x-user-id": getUserId() }),
        ...(getBrowserTimezone() && { timezone: getBrowserTimezone() }),
    }
})


apiinstanceFormData.interceptors.request.use(
    config => {
        const token = getFromStorage({ key: StorageConstant?.ACCESS_TOKEN, type: 'cookies' });
        const currentLang = changeLanguage();
        const currentUserId = getUserId();
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        if (currentLang) {
            config.headers['lang'] = currentLang;
        } else if (config.headers?.lang) {
            delete config.headers.lang;
        }
        if (currentUserId) {
            config.headers['x-user-id'] = currentUserId;
        } else if (config.headers?.['x-user-id']) {
            delete config.headers['x-user-id'];
        }
        const browserTimezone = getBrowserTimezone();
        if (browserTimezone) {
            config.headers.timezone = browserTimezone;
        } else if (config.headers?.timezone) {
            delete config.headers.timezone;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

/** Sign-up: keep existing keys; add `name`, `firstName`, `lastName` for backends that require `name` NOT NULL */
function augmentSignUpPayload(params: any): any {
    if (params == null || typeof params !== 'object') return params;
    if (typeof FormData !== 'undefined' && params instanceof FormData) return params;
    const p = { ...params };
    const fn = p.first_name != null ? String(p.first_name).trim() : '';
    const ln = p.last_name != null ? String(p.last_name).trim() : '';
    if (fn || ln) {
        p.name = [fn, ln].filter(Boolean).join(' ').trim();
        p.firstName = fn;
        p.lastName = ln;
        return p;
    }
    const co = p.company_name != null ? String(p.company_name).trim() : '';
    if (co) {
        p.name = co;
        p.firstName = '';
        p.lastName = '';
    }
    return p;
}

/** When `false`, suppress success/error toasts for this request (caller handles UX). */
const allowToast = (toastStatus: any) => toastStatus !== false;

/** Nest/axios bodies: `message` string, `message` string[], or `custom_message`. */
function pickApiErrorMessage(data: any): string {
  if (!data) return "";
  const m = data.message;
  if (typeof m === "string" && m.trim()) return m.trim();
  if (Array.isArray(m)) {
    const parts = m
      .map((x) => (typeof x === "string" ? x : x != null ? String(x) : ""))
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  if (typeof data.custom_message === "string" && data.custom_message.trim()) return data.custom_message.trim();
  if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
  return "";
}

const isLoginSignInUrl = (url: string) => typeof url === 'string' && url.includes('sign-in');

const loginFeMs = () =>
    typeof performance !== 'undefined' ? performance.now() : Date.now();

const loginFeLog = (url: string, phase: string, extra?: Record<string, unknown>) => {
    if (!isLoginSignInUrl(url)) return;
    const payload = { phase, ...extra };
    console.log(`[Login][WEB FE] ${phase}`, payload);
};

export const POSTAPI = (URL: string, params: any, toastStatus: any = true) => {
    const body =
        typeof URL === 'string' && URL.includes('sign-up')
            ? augmentSignUpPayload(params)
            : params;
    const t0 = loginFeMs();
    loginFeLog(URL, 'API started', { url: URL, ms: 0 });
    loginFeLog(URL, 'API running — axios request in flight', { ms: Math.round((loginFeMs() - t0) * 100) / 100 });

    const promise = apiinstance.post(URL, body, {
        validateStatus: status => status >= 200 && status < 600
    });
    const dataPromise = promise.then((response:any) => {
        if (isLoginSignInUrl(URL)) {
            console.log('[Login][WEB FE] API running — HTTP response received', {
                ms: Math.round((loginFeMs() - t0) * 100) / 100,
                httpStatus: response?.status,
            });
        }
        return response;
    }).then((response:any) => {
        if (isQuotesListApiUrl(URL)) {
            settleQuotesListVisit();
        }
        if (isOrdersListApiUrl(URL)) {
            settleOrdersListVisit();
        }
        if (isLoginSignInUrl(URL)) {
            console.log('[Login][WEB FE] API running — parsing response body', {
                ms: Math.round((loginFeMs() - t0) * 100) / 100,
            });
        }
        
        if (response?.status === CODE?.OK_CODE || response?.status === CODE?.CREATED) {
            if (response?.data?.status) {
                if (toastStatus) {
                    toast.success(response.data?.message);
                }
                return response.data;
            }
            else if (response?.data?.status_code === CODE?.NO_CONTENT) {
                return response?.data;

            }
            else {
                const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
                if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
                return response.data;
            }
        }
        else if (response?.status === CODE?.BAD_REQUEST_CODE) {
            const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
            if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
            return response?.data;
        }
        else if (response?.status === CODE?.UNAUTHORIZED_CODE) {
            handleLogout()
        }
        else if (response?.status === CODE?.INTERNAL_SERVER_ERROR) {
            const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
            if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
            return response?.data;

        }
        else if (response?.status === CODE?.VALIDATION_CODE) {
            if (isLoginSignInUrl(URL)) {
                const d = response?.data;
                const msg =
                    typeof d?.message === 'string'
                        ? d.message
                        : Array.isArray(d?.message)
                          ? d.message.join('; ')
                          : JSON.stringify(d?.message ?? d);
                console.warn('[Login][WEB FE] Server returned 422 (auth / validation)', {
                    ms: Math.round((loginFeMs() - t0) * 100) / 100,
                    message: msg,
                    status_code: d?.status_code,
                    error: d?.error,
                });
            }
            const errMsg422 = pickApiErrorMessage(response.data) || response.data?.message;
            if (allowToast(toastStatus)) toast.error(errMsg422 || VALIDATION_MESSAGE?.CATCH_ERROR);
            return response?.data;

        }
        else {
            if (isLoginSignInUrl(URL)) {
                console.warn('[Login][WEB FE] Unexpected HTTP status for sign-in', {
                    ms: Math.round((loginFeMs() - t0) * 100) / 100,
                    httpStatus: response?.status,
                    data: response?.data,
                });
            }
            if (allowToast(toastStatus)) toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
        }
    }).catch((error:any) => {
        if (isQuotesListApiUrl(URL)) {
            settleQuotesListVisit();
        }
        if (isOrdersListApiUrl(URL)) {
            settleOrdersListVisit();
        }
        // return error;   
        if (error?.response?.status === CODE?.UNAUTHORIZED_CODE) {
            handleLogout()
        }
        if (allowToast(toastStatus)) toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
    });
    if (isLoginSignInUrl(URL)) {
        void dataPromise.then(
            (data) => {
                console.log('[Login][WEB FE] API ended — POSTAPI promise settled (returning to caller)', {
                    ms: Math.round((loginFeMs() - t0) * 100) / 100,
                    bodyStatus: (data as any)?.status,
                });
            },
            () => {
                console.log('[Login][WEB FE] API ended — POSTAPI rejected', {
                    ms: Math.round((loginFeMs() - t0) * 100) / 100,
                });
            }
        );
    }
    return dataPromise;
};

export const POSTAPIFORMDATA = (URL: string, params: any, toastStatus: any = true) => {
    const body =
        typeof URL === 'string' && URL.includes('sign-up')
            ? augmentSignUpPayload(params)
            : params;
    const promise = apiinstanceFormData.post(URL, body, {
        validateStatus: status => status >= 200 && status < 600
    });
    const dataPromise = promise.then((response:any) => response).then((response:any) => {

        if (response?.status === CODE?.OK_CODE || response?.status === CODE?.CREATED) {
            if (response?.data?.status) {
                if (toastStatus) {
                    toast.success(response.data?.message);
                }
                return response.data;
            }
            else if (response?.data?.status_code === CODE?.NO_CONTENT) {
                return response?.data;

            }
            else {
                const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
                if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
                return response.data;
            }
        }
        else if (response?.status === CODE?.BAD_REQUEST_CODE) {
            const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
            if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
            return response?.data;
        }
        else if (response?.status === CODE?.UNAUTHORIZED_CODE) {
            handleLogout()
        }
        else if (response?.status === CODE?.INTERNAL_SERVER_ERROR) {
            const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
            if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
            return response?.data;

        }
        else if (response?.status === CODE?.VALIDATION_CODE) {
            const errMsg = pickApiErrorMessage(response.data) || response.data?.message;
            if (allowToast(toastStatus)) toast.error(errMsg || VALIDATION_MESSAGE?.CATCH_ERROR);
            return response?.data;

        }
        else {
            if (allowToast(toastStatus)) toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
        }
    }).catch((error:any) => {

        if (error?.response?.status === CODE?.UNAUTHORIZED_CODE) {
            handleLogout()
        }
        if (allowToast(toastStatus)) toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
    });
    return dataPromise;
};


export const GETAPI = (URL: string, params: any = {}, toastStatus: any = false) => {
    const promise = apiinstance.get(URL, params)

    const dataPromise = promise.then((response:any) => response).then((response:any) => {

        if (response?.status === 200 || response?.status === 201) {
            //  toast.success(response.data?.message)
            if (toastStatus) { toast.success(response.data?.message) }
            return response.data;
        } else {
            if (allowToast(toastStatus)) toast.error(response.data?.message);
            return response?.data;
        }
    }).catch((error:any) => {
        if (error?.response?.status === CODE?.UNAUTHORIZED_CODE) {
            handleLogout()
        }
        if (allowToast(toastStatus)) toast.error(VALIDATION_MESSAGE?.CATCH_ERROR);
    })
    return dataPromise;
}

